import { z } from 'zod';

/**
 * URL search-param schema for `/transactions`, so filter state survives reloads
 * and is shareable; mirrors `clientRequestsSearchSchema`.
 *
 * Fields use `.optional().catch(undefined)`, never `.catch(default)`: a plain
 * default makes missing indistinguishable from malformed, so `validateSearch`
 * re-adds it on every navigation and `useUrlFilters` can't omit default-valued
 * keys for a clean URL after Reset. `useUrlFilters` supplies defaults on read.
 */
export const transactionsSearchSchema = z.object({
  /** DEV-4396: `incomeLinking` opens that tab; absent = the ledger. */
  tab: z.enum(['incomeLinking']).optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  /** Which date the period range narrows: `creation` (default), `trade` or `settlement`. */
  periodField: z.enum(['creation', 'trade', 'settlement']).optional().catch(undefined),
  /** Preset id (e.g. `'7d'`), `'custom'`, or `null` for "All"; absent -> `null` via `useUrlFilters`. */
  dateRangePreset: z.string().nullable().optional().catch(undefined),
  /** Populated only when `dateRangePreset === 'custom'` (ISO `yyyy-MM-dd`). */
  dateFrom: z.string().optional().catch(undefined),
  dateTo: z.string().optional().catch(undefined),
  accountIds: z.array(z.string()).optional().catch(undefined),
  investorIds: z.array(z.string()).optional().catch(undefined),
  /**
   * Investor display name passed by the Investor Profile shortcut, pinned into
   * the Investors filter chip. Not filter state — it never narrows rows.
   */
  investorName: z.string().optional().catch(undefined),
  currencies: z.array(z.string()).optional().catch(undefined),
  exchanges: z.array(z.string()).optional().catch(undefined),
  sources: z.array(z.string()).optional().catch(undefined),
  types: z.array(z.string()).optional().catch(undefined),
  transactionClasses: z.array(z.string()).optional().catch(undefined),
  symbols: z.array(z.string()).optional().catch(undefined),
  statuses: z.array(z.string()).optional().catch(undefined),
  /** DEV-4067: "Show deleted" toggle, separate from `statuses`. */
  deleted: z.boolean().optional().catch(undefined),
  authors: z.array(z.string()).optional().catch(undefined),
  hasDocs: z.boolean().optional().catch(undefined),
});

export type TransactionsSearch = z.infer<typeof transactionsSearchSchema>;
